import { DiagnosisRow, DIAGNOSIS_STATUS } from './useDiagnosisData';

/**
 * Filters diagnosis rows based on search query and status filters
 * @param data Array of diagnosis rows to filter
 * @param searchQuery Search query string
 * @param filters Array of status filters ('AI Recommended' | 'Existing - Found in EMR' | null)
 * @returns Filtered array of diagnosis rows
 */
export const filterDiagnosisRows = (
  data: DiagnosisRow[],
  searchQuery: string,
  filters: ('AI Recommended' | 'Existing')[] | null,
): DiagnosisRow[] => {
  // Split search query into keywords and filter out empty strings
  const searchKeywords = searchQuery
    .toLowerCase()
    .split(' ')
    .filter((keyword) => keyword.length > 0);

  const filteredRows = data.filter((row) => {
    // If no search keywords, only apply status filters
    if (searchKeywords.length === 0) {
      return matchesStatusFilters(row, filters);
    }

    // Create searchable text from code, description, and category
    const searchableText = `${row.code.toLowerCase()} ${row.description.toLowerCase()} ${row.category.toLowerCase()}`;

    // Check if all keywords match anywhere in the searchable text
    const matchesSearch = searchKeywords.every((keyword) => searchableText.includes(keyword));

    return matchesSearch && matchesStatusFilters(row, filters);
  });

  return filteredRows;
};

/**
 * Checks if a row matches any of the status filters
 * @param row Diagnosis row to check
 * @param filters Array of status filters to apply
 * @returns boolean indicating if the row matches any of the filters
 */
const matchesStatusFilters = (row: DiagnosisRow, filters: ('AI Recommended' | 'Existing')[] | null): boolean => {
  if (!filters || filters.length === 0) {
    return true;
  }

  return filters.some((filter) => {
    if (filter === 'AI Recommended') {
      return row.status.includes('AI Recommended');
    }

    if (filter === 'Existing') {
      return row.status === DIAGNOSIS_STATUS.EXISTING;
    }

    return true;
  });
};
